import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic: Comic | undefined = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <SEOHead
          title="页面未找到 - 韩国漫画网"
          description="抱歉，您访问的漫画页面不存在。"
          keywords="韩国漫画网,韩漫"
          canonicalUrl="https://www.koreanmanhwa.com/"
        />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-5">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mt-2 mb-5">抱歉，您访问的漫画不存在或已下架。</p>
            <Link to="/" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const seoTitle = `${comic.title} - 韩国漫画在线阅读 - 韩国漫画网`;
  const seoDesc = `${comic.title}是由${comic.author}创作的韩国漫画。${comic.description.slice(0, 120)}... 来韩国漫画网免费在线阅读，体验最佳韩漫阅读。`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫在线阅读,${comic.tags.join(',')}`}
        canonicalUrl={`https://www.koreanmanhwa.com/comic/${comic.id}`}
        ogTitle={seoTitle}
        ogDescription={seoDesc}
        ogType="article"
        ogUrl={`https://www.koreanmanhwa.com/comic/${comic.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ComicStory",
            "name": comic.title,
            "author": {
              "@type": "Person",
              "name": comic.author
            },
            "description": comic.description,
            "genre": comic.tags,
            "url": `https://www.koreanmanhwa.com/comic/${comic.id}`,
            "dateModified": comic.updatedAt
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.koreanmanhwa.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": `${comic.category === 'romance' ? '恋爱' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'action' ? '动作' : comic.category === 'drama' ? '剧情' : comic.category === 'comedy' ? '搞笑' : comic.category === 'thriller' ? '悬疑' : '全部'}韩国漫画`,
                "item": `https://www.koreanmanhwa.com/category/${comic.category}`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": comic.title,
                "item": `https://www.koreanmanhwa.com/comic/${comic.id}`
              }
            ]
          }
        ]}
      />

      <Navbar />
      <main className="min-h-screen">
        <article className="max-w-[1280px] mx-auto px-4 md:px-6 pt-20 pb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category === 'romance' ? '恋爱' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'action' ? '动作' : comic.category === 'drama' ? '剧情' : comic.category === 'comedy' ? '搞笑' : comic.category === 'thriller' ? '悬疑' : '全部'}韩国漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col md:flex-row gap-8 mb-12">
            <div className="flex-shrink-0 w-48 md:w-56">
              <div className="relative aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩国漫画封面`}
                  title={`${comic.title} - 韩国漫画在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
                {comic.isNew && (
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded">
                    NEW
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <header>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  <strong>{comic.title}</strong>
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{comic.author} · 著</p>
              </header>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="flex items-center gap-1 text-sm font-semibold text-amber-500">
                  <i className="ri-star-fill"></i>
                  {comic.rating}
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>
                  {(comic.views / 10000).toFixed(0)}万阅读
                </span>
                <span className={`px-2.5 py-1 rounded text-xs font-medium ${
                  comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-5">
                <h2 className="text-sm font-semibold text-foreground-950 mb-2">
                  <strong>作品简介</strong>
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-5 text-xs text-foreground-500">
                最近更新：{comic.updatedAt}
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.id}`}
                  className="px-6 py-2.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  开始阅读第一话
                </Link>
                <button className="px-6 py-2.5 bg-secondary-100 text-secondary-700 font-semibold rounded-lg hover:bg-secondary-200 transition-all text-sm whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line mr-1.5"></i>收藏
                </button>
              </div>
            </div>
          </div>

          <section className="mb-12">
            <h2 className="text-lg font-bold text-foreground-950 mb-5 font-heading flex items-center gap-2">
              <i className="ri-list-check text-primary-500"></i>
              <strong>章节列表</strong>
              <span className="text-xs text-foreground-500 font-normal">（共 {comic.chapters.length} 话）</span>
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/60 divide-y divide-background-200/60">
              {comic.chapters.map((chapter, index) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-background-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 text-xs font-bold flex-shrink-0">
                      {comic.chapters.length - index}
                    </span>
                    <span className="text-sm font-medium text-foreground-800 truncate">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-foreground-500 flex-shrink-0 ml-3">{chapter.date}</span>
                </div>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mb-12">
              <h2 className="text-lg font-bold text-foreground-950 mb-5 font-heading">
                <strong>相关推荐</strong>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}